/**
 * Pill filters — apply a facet change without reloading the page.
 *
 * The pills are plain <label>s (see snippets/filter-group.liquid), so unlike the theme's
 * own inputs they are not inside a <facet-inputs-component on:change="/updateFilters">
 * and nothing wires them to the theme's update path.
 *
 * We therefore call facets-form-component.updateFilters() ourselves. That is the fast
 * path the theme uses: history.pushState + a Section Rendering API fetch wrapped in a
 * View Transition (assets/facets.js). Submitting the form instead would trigger a full
 * page navigation — which is why filtering felt slow.
 *
 * Delegated from the document so pills survive the section being re-rendered.
 */

/**
 * Price brackets are nameless checkboxes paired with hidden gte/lte inputs.
 * A disabled input is left out of FormData, so enabling exactly the checked bracket's
 * inputs is what applies it. Brackets are mutually exclusive.
 *
 * @param {Element} group
 * @param {HTMLInputElement | null} chosen
 */
function syncPriceBrackets(group, chosen) {
  group.querySelectorAll('.facets__price-pill').forEach((pill) => {
    const checkbox = pill.querySelector('input.facets__price-input');
    if (!(checkbox instanceof HTMLInputElement)) return;

    if (chosen && checkbox !== chosen) checkbox.checked = false;

    pill.querySelectorAll('input[type="hidden"]').forEach((hidden) => {
      /** @type {HTMLInputElement} */ (hidden).disabled = !checkbox.checked;
    });
  });
}

/**
 * Applies the current form state. Prefers the theme's in-place update; falls back to a
 * normal submit only if the custom element is missing or not yet upgraded.
 *
 * @param {HTMLFormElement} form
 */
function applyFilters(form) {
  const facetsForm = form.closest('facets-form-component');

  if (facetsForm && typeof (/** @type {any} */ (facetsForm).updateFilters) === 'function') {
    /** @type {any} */ (facetsForm).updateFilters();
    return;
  }

  form.requestSubmit ? form.requestSubmit() : form.submit();
}

document.addEventListener('change', function (event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (!input.classList.contains('facets__pill-input')) return;

  const form = input.closest('form');
  if (!form) return;

  const group = input.closest('.facets__filter-group');

  // "Tous" carries no name/value — it means "no value selected in this group",
  // so it clears its siblings rather than selecting them.
  if (input.classList.contains('facets__pill-tous-input')) {
    if (group) {
      group
        .querySelectorAll('input.facets__pill-input:not(.facets__pill-tous-input)')
        .forEach((sibling) => {
          /** @type {HTMLInputElement} */ (sibling).checked = false;
        });
      syncPriceBrackets(group, null);
    }
    input.checked = false;
  } else if (group && input.classList.contains('facets__price-input')) {
    syncPriceBrackets(group, input);
  }

  applyFilters(form);
});
